import type { ReactNode } from "react";
import { ArrowLeft, CalendarDays, Clock3 } from "lucide-react";
import { Link, useParams } from "wouter";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { CtaBand } from "@/components/site/CtaBand";
import { SiteLayout } from "@/components/site/SiteLayout";
import { blogPosts } from "@/content/blogPosts";
import { useDocumentTitle } from "@/components/Head";

function formatDate(value: string) {
  return new Date(`${value}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function linkify(text: string): ReactNode[] {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.filter(Boolean).map((part, index) => {
    if (!part.startsWith("http")) return part;
    const cleanUrl = part.replace(/[.,;]+$/, "");
    const suffix = part.slice(cleanUrl.length);
    return (
      <span key={`${cleanUrl}-${index}`}>
        <a href={cleanUrl} target="_blank" rel="noreferrer">{cleanUrl}</a>{suffix}
      </span>
    );
  });
}

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((item) => item.slug === slug);
  useDocumentTitle(post?.title);

  if (!post) {
    return (
      <SiteLayout>
        <section className="page-section page-section-white article-not-found">
          <div className="container">
            <p className="section-kicker">Article not found</p>
            <h1>That article is not in the archive.</h1>
            <p>The address may have changed or the link may be incomplete.</p>
            <Link className="button-primary" href="/blog"><ArrowLeft size={17} /> Return to the blog</Link>
          </div>
        </section>
      </SiteLayout>
    );
  }

  const readingMinutes = Math.max(3, Math.round(post.blocks.reduce((total, block) => total + block.text.split(/\s+/).length, 0) / 220));

  return (
    <SiteLayout>
      <article>
        <div className="article-hero">
          <div className="article-orbit" aria-hidden="true" />
          <div className="container">
            <Breadcrumbs items={[{ label: "Blog", href: "/blog" }, { label: post.title }]} />
            <p className="section-kicker">From the CORE Strategies PT archive</p>
            <h1>{post.title}</h1>
            <div className="article-byline">
              <span><CalendarDays size={16} /> {formatDate(post.published)}</span>
              <span><Clock3 size={16} /> About {readingMinutes} min read</span>
              <span>{post.author}</span>
            </div>
          </div>
        </div>

        <div className="container article-layout">
          <aside className="historical-note">
            <strong>Historical article</strong>
            <p>This article was originally published in {post.published.slice(0, 4)} and is preserved from CORE’s previous website. Statistics, clinical language, external resources, programs, and recommendations may have changed. It is educational and not individualized medical advice.</p>
            <Link href="/blog"><ArrowLeft size={15} /> Back to all articles</Link>
          </aside>
          <div className="article-body">
            {post.blocks.map((block, index) => {
              if (block.type === "heading") return <h2 key={`${block.text}-${index}`}>{block.text}</h2>;
              if (block.type === "reference") return <div className="article-reference" key={`${block.text}-${index}`}>{linkify(block.text)}</div>;
              return <p key={`${block.text.slice(0, 34)}-${index}`}>{linkify(block.text)}</p>;
            })}
            <div className="article-disclaimer">
              <strong>Information, not a diagnosis.</strong>
              <p>Website articles are for general education and do not replace evaluation or advice from an appropriate healthcare professional.</p>
            </div>
          </div>
        </div>
      </article>

      <CtaBand title="Your movement story is personal." body="If an article raises a question about your own symptoms, goals, or activity, the office can help you decide what kind of conversation makes sense." />
    </SiteLayout>
  );
}
